'use client';

import {useId} from 'react';

type DropdownFieldProps = {
  value: string | null | undefined;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  label: string;
  required?: boolean;
  options: string[];
};

export function DropdownField({
  value,
  onChange,
  error,
  disabled,
  label,
  required,
  options,
}: DropdownFieldProps) {
  const id = useId();

  // Surface a current value that isn't a valid option (e.g. a staged Otto
  // write proposing one that doesn't exist) as its own entry — otherwise the
  // <select> silently falls back to the placeholder while holding the
  // invalid value, so the user can't tell anything is wrong or which entry
  // to change.
  const hasInvalidValue = Boolean(value) && !options.includes(String(value));

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <select
        id={id}
        value={String(value ?? '')}
        onChange={(e) => {
          onChange(e.target.value || null);
        }}
        disabled={disabled}
        className={`w-full rounded-md border px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-50' : ''}`}
      >
        <option value="">Select...</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
        {hasInvalidValue && (
          <option
            value={String(value)}
          >{`${value} (not a valid option)`}</option>
        )}
      </select>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
